import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ExternalLink,
  Eye,
  EyeOff,
  FileClock,
  Loader2,
  Pencil,
  Plus,
  Receipt,
  RefreshCw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  bulkSetInvoiceVisibility,
  deleteInvoice,
  getInvoiceSignedUrl,
  listInvoices,
  type AdminInvoiceListItem,
  type InvoiceStatus,
} from "@/lib/invoices.functions";
import { listClients } from "@/lib/customers.functions";
import { InvoiceStatusBadge } from "@/components/invoices/InvoiceStatusBadge";
import { hiddenLabel } from "@/components/invoices/hidden-reason";
import { confirmDialog } from "@/lib/confirm-dialog";
import { invoiceKindLabel } from "@/lib/cockpit-invoices";
import { getLatestMocoSyncLog, triggerMocoSync } from "@/lib/moco.functions";

export const Route = createFileRoute("/_admin/admin/invoices/")({
  head: () => ({
    meta: [
      { title: "Rechnungen – Admin – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: InvoicesAdminPage,
});

const dateFmt = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

function formatAmount(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "EUR",
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

function InvoicesAdminPage() {
  const listFn = useServerFn(listInvoices);
  const clientsFn = useServerFn(listClients);
  const deleteFn = useServerFn(deleteInvoice);
  const signFn = useServerFn(getInvoiceSignedUrl);
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [clientId, setClientId] = useState<string | "">("");
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    setSelected(new Set());
  }, [search, clientId]);

  const { data: items, isLoading, error } = useQuery({
    queryKey: ["admin-invoices", { search, clientId }],
    queryFn: () =>
      listFn({
        data: {
          search: search.trim() || undefined,
          clientId: clientId || null,
        },
      }),
  });
  const { data: clients } = useQuery({
    queryKey: ["admin-clients-list-slim"],
    queryFn: () => clientsFn(),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-invoices"] }),
  });

  const syncFn = useServerFn(triggerMocoSync);
  const latestLogFn = useServerFn(getLatestMocoSyncLog);
  const { data: latestLog, refetch: refetchLatest } = useQuery({
    queryKey: ["moco-latest-log"],
    queryFn: () => latestLogFn(),
    staleTime: 60_000,
  });
  const syncMutation = useMutation({
    mutationFn: () => syncFn(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["admin-invoices"] });
      refetchLatest();
      const parts = [
        `neu ${res.invoices_created}`,
        `aktualisiert ${res.invoices_updated}`,
        `übersprungen ${res.invoices_skipped}`,
        `Fehler ${res.invoices_errors}`,
      ];
      alert(`Moco-Sync abgeschlossen — ${parts.join(" · ")}`);
    },
    onError: (e) => alert(e instanceof Error ? e.message : String(e)),
  });

  const bulkFn = useServerFn(bulkSetInvoiceVisibility);
  const bulkMutation = useMutation({
    mutationFn: (payload: { ids: string[]; visible: boolean }) =>
      bulkFn({ data: payload }),
    onSuccess: (res, vars) => {
      qc.invalidateQueries({ queryKey: ["admin-invoices"] });
      setSelected(new Set());
      toast.success(
        vars.visible
          ? `${res.updated} Rechnung(en) wieder sichtbar.`
          : `${res.updated} Rechnung(en) versteckt.`,
      );
    },
    onError: (e) =>
      toast.error(e instanceof Error ? e.message : "Aktion fehlgeschlagen."),
  });

  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    const ids = (items ?? []).map((i) => i.id);
    setSelected((prev) => (prev.size === ids.length ? new Set() : new Set(ids)));
  }

  async function onDelete(item: AdminInvoiceListItem) {
    if (
      !(await confirmDialog({
        title: `Rechnung „${item.invoice_number}" löschen?`,
        description: "Die PDF-Datei wird ebenfalls entfernt.",
      }))
    )
      return;
    try {
      await removeMutation.mutateAsync(item.id);
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    }
  }

  async function onPreview(item: AdminInvoiceListItem) {
    try {
      const res = await signFn({ data: { id: item.id } });
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold md:text-4xl">
            Rechnungen
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Manuelle Rechnungs-Uploads für Kundinnen und Kunden. PDFs bleiben
            privat, Kunden sehen sie ausschließlich im Cockpit.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-semibold text-foreground hover:bg-secondary disabled:opacity-50"
          >
            {syncMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Jetzt synchronisieren
          </button>
          <Link
            to="/admin/invoices/sync-log"
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-semibold text-foreground hover:bg-secondary"
          >
            <FileClock className="h-4 w-4" /> Sync-Protokoll
          </Link>
          <Link
            to="/admin/invoices/$id"
            params={{ id: "new" }}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground"
          >
            <Plus className="h-4 w-4" /> Neue Rechnung
          </Link>
        </div>
      </header>

      {latestLog && (
        <p className="mt-3 text-xs text-muted-foreground">
          Letzter Sync: {new Date(latestLog.started_at).toLocaleString("de-DE")} ·
          Auslöser {latestLog.trigger} · Status {latestLog.status} · neu{" "}
          {latestLog.invoices_created} · aktualisiert {latestLog.invoices_updated}{" "}
          · übersprungen {latestLog.invoices_skipped} · Fehler{" "}
          {latestLog.invoices_errors}
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechnungsnummer"
            className="rounded-md border border-input bg-background py-2 pl-8 pr-3 text-sm"
          />
        </div>
        <select
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          className="rounded-md border border-input bg-background px-2 py-2 text-sm"
        >
          <option value="">Alle Kunden</option>
          {(clients ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.organization_name}
            </option>
          ))}
        </select>
        {(search || clientId) && (
          <button
            onClick={() => {
              setSearch("");
              setClientId("");
            }}
            className="text-xs text-muted-foreground underline"
          >
            Filter zurücksetzen
          </button>
        )}
      </div>

      {error && (
        <div className="mt-6 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {selected.size > 0 && (
        <div
          role="region"
          aria-live="polite"
          className="sticky top-2 z-10 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 px-4 py-2 text-sm"
        >
          <span className="font-semibold text-primary">
            {selected.size} ausgewählt
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={bulkMutation.isPending}
              onClick={() =>
                bulkMutation.mutate({
                  ids: Array.from(selected),
                  visible: true,
                })
              }
              className="inline-flex items-center gap-1 rounded-md bg-brand-button px-3 py-1.5 text-xs font-semibold text-brand-button-foreground disabled:opacity-60"
            >
              <Eye className="h-3.5 w-3.5" /> Wieder sichtbar machen
            </button>
            <button
              type="button"
              disabled={bulkMutation.isPending}
              onClick={() =>
                bulkMutation.mutate({
                  ids: Array.from(selected),
                  visible: false,
                })
              }
              className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary disabled:opacity-60"
            >
              <EyeOff className="h-3.5 w-3.5" /> Verstecken
            </button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" /> Auswahl aufheben
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 overflow-hidden rounded-lg border border-border bg-background">
        {isLoading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Laden …
          </div>
        ) : !items || items.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">
            Noch keine Rechnungen angelegt.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th scope="col" className="w-10 px-3 py-3">
                  <input
                    type="checkbox"
                    aria-label="Alle auswählen"
                    checked={
                      items.length > 0 && selected.size === items.length
                    }
                    ref={(el) => {
                      if (el)
                        el.indeterminate =
                          selected.size > 0 && selected.size < items.length;
                    }}
                    onChange={toggleAll}
                    className="h-4 w-4 cursor-pointer rounded border-border"
                  />
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Nummer
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Kunde
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Datum
                </th>
                <th scope="col" className="px-4 py-3 text-right font-semibold">
                  Betrag brutto
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Quelle
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Status
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Sichtbar
                </th>
                <th scope="col" className="px-4 py-3 text-right font-semibold">
                  <span className="sr-only">Aktionen</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((it) => (
                <tr key={it.id} className="align-middle">
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      aria-label={`Rechnung ${it.invoice_number} auswählen`}
                      checked={selected.has(it.id)}
                      onChange={() => toggleRow(it.id)}
                      className="h-4 w-4 cursor-pointer rounded border-border"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-2 font-semibold text-primary">
                      <Receipt className="h-4 w-4 text-muted-foreground" aria-hidden />
                      {it.invoice_number}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-foreground">
                    {it.client?.organization_name ?? "–"}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {dateFmt.format(new Date(it.invoice_date))}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {formatAmount(it.amount_gross_cents, it.currency)}
                    {invoiceKindLabel(it) && (
                      <span className="ml-2 whitespace-nowrap text-xs font-semibold text-muted-foreground">
                        {invoiceKindLabel(it)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        "inline-flex rounded px-2 py-0.5 text-xs font-semibold " +
                        (it.source === "manual"
                          ? "bg-secondary text-foreground"
                          : "bg-brand-kommunikation/20 text-foreground")
                      }
                    >
                      {it.source === "manual" ? "manuell" : "moco"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {it.source === "moco" ? (
                      <InvoiceStatusBadge status={it.status} />
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {it.visible ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-foreground">
                          <Eye className="h-3.5 w-3.5" aria-hidden /> sichtbar
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground">
                          <EyeOff className="h-3.5 w-3.5" aria-hidden />{" "}
                          {hiddenLabel(it.hidden_reason)}
                        </span>
                      )}
                      {(
                        <button
                          type="button"
                          onClick={() =>
                            bulkMutation.mutate({
                              ids: [it.id],
                              visible: !it.visible,
                            })
                          }
                          disabled={bulkMutation.isPending}
                          className="rounded border border-border px-2 py-0.5 text-[11px] font-semibold hover:bg-secondary disabled:opacity-60"
                        >
                          {it.visible ? "Verstecken" : "Wieder sichtbar machen"}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => onPreview(it)}
                        className="inline-flex items-center gap-1 rounded border border-border px-3 py-1 text-xs font-semibold hover:bg-secondary"
                      >
                        <ExternalLink className="h-3.5 w-3.5" /> PDF
                      </button>
                      <Link
                        to="/admin/invoices/$id"
                        params={{ id: it.id }}
                        className="inline-flex items-center gap-1 rounded border border-border px-3 py-1 text-xs font-semibold hover:bg-secondary"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Bearbeiten
                      </Link>
                      <button
                        onClick={() => onDelete(it)}
                        aria-label="Löschen"
                        className="rounded border border-destructive/40 px-2 py-1 text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
